import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProfile, ProfileData, updateName, updateDepartment, requestEmailChange, verifyEmailChange } from '../services/profileService';
import { ROLES } from '../constants/roles';
import { User, Mail, Shield, Briefcase, Award, Loader, AlertCircle, Edit2, Save, X, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedDepartment, setEditedDepartment] = useState('');
  const [emailVerificationStatus, setEmailVerificationStatus] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [devOtp, setDevOtp] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await getProfile();
        setProfileData(data);
      } catch (err: any) {
        // If endpoint doesn't exist (404), silently fall back to AuthContext data
        // Only show error for other types of failures
        if (err.response?.status === 404) {
          console.log('Profile endpoint not available, using AuthContext data');
          // profileData will remain null, and we'll use user from AuthContext
        } else {
          console.error('Failed to fetch profile:', err);
          setError(err.response?.data?.message || 'Failed to load profile data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const displayData = profileData || user;
    if (displayData && !isEditMode) {
      setEditedName(displayData.name || '');
      setEditedEmail(displayData.email || '');
      // Use department if available, otherwise fall back to specialization for backward compatibility
      const dept = (displayData as ProfileData).department || (displayData as any).specialization || '';
      setEditedDepartment(dept);
    }
  }, [profileData, user, isEditMode]);

  const handleEditClick = () => {
    const displayData = profileData || user;
    setEditedName(displayData?.name || '');
    setEditedEmail(displayData?.email || '');
    // Use department if available, otherwise fall back to specialization for backward compatibility
    const dept = (displayData as ProfileData)?.department || (displayData as any)?.specialization || '';
    setEditedDepartment(dept);
    setIsEditMode(true);
    setError(null);
  };

  const handleCancel = () => {
    const displayData = profileData || user;
    setEditedName(displayData?.name || '');
    setEditedEmail(displayData?.email || '');
    // Use department if available, otherwise fall back to specialization for backward compatibility
    const dept = (displayData as ProfileData)?.department || (displayData as any)?.specialization || '';
    setEditedDepartment(dept);
    setIsEditMode(false);
    setError(null);
    setEmailVerificationStatus(null);
    setOtp('');
    setDevOtp('');
    setOtpError(null);
    setPendingEmail(null);
    setOtpVerified(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const displayData = profileData || user;
      const nameChanged = editedName !== (displayData?.name || '');
      const emailChanged = editedEmail !== (displayData?.email || '');
      const currentDepartment = (displayData as ProfileData)?.department || (displayData as any)?.specialization || '';
      const departmentChanged = editedDepartment.trim() !== currentDepartment;

      if (!nameChanged && !emailChanged && !departmentChanged) {
        setIsEditMode(false);
        setIsSaving(false);
        return;
      }

      // Update name if changed
      if (nameChanged) {
        try {
          await updateName(editedName);
          updateUser({ name: editedName });
          if (profileData) {
            setProfileData({ ...profileData, name: editedName });
          }
          toast.success('Name updated successfully');
        } catch (err: any) {
          // If endpoint doesn't exist (404), update AuthContext directly
          if (err.response?.status === 404) {
            console.log('Name update endpoint not available, updating locally');
            updateUser({ name: editedName });
            if (profileData) {
              setProfileData({ ...profileData, name: editedName });
            }
            toast.success('Name updated locally (backend endpoint not available)');
          } else {
            console.error('Failed to update name:', err);
            toast.error(err.response?.data?.message || 'Failed to update name');
            setIsSaving(false);
            return;
          }
        }
      }

      // Update department if changed (only for doctors)
      if (departmentChanged && role === ROLES.DOCTOR) {
        try {
          const trimmedDepartment = editedDepartment.trim();
          await updateDepartment(trimmedDepartment);
          if (profileData) {
            setProfileData({ ...profileData, department: trimmedDepartment });
          }
          toast.success('Department updated successfully');
        } catch (err: any) {
          // If endpoint doesn't exist (404), update locally
          if (err.response?.status === 404) {
            console.log('Department update endpoint not available, updating locally');
            const trimmedDepartment = editedDepartment.trim();
            if (profileData) {
              setProfileData({ ...profileData, department: trimmedDepartment });
            }
            toast.success('Department updated locally (backend endpoint not available)');
          } else {
            console.error('Failed to update department:', err);
            toast.error(err.response?.data?.message || 'Failed to update department');
            setIsSaving(false);
            return;
          }
        }
      }

      // Request email change if changed
      if (emailChanged) {
        // If OTP was already verified, email is already updated, so skip request
        if (otpVerified) {
          // Email was already verified, just close edit mode
          setIsEditMode(false);
          setIsSaving(false);
          setOtpVerified(false);
          setEmailVerificationStatus(null);
          setPendingEmail(null);
          setDevOtp('');
          return;
        }
        
        try {
          const result = await requestEmailChange(editedEmail);
          setEmailVerificationStatus('pending');
          setPendingEmail(editedEmail);
          setOtp('');
          setOtpError(null);
          setOtpVerified(false);
          if (result.otp) setDevOtp(result.otp);
          toast.success('Verification email sent to new address');
          // Keep edit mode open to allow OTP entry
          setIsSaving(false);
          return;
        } catch (err: any) {
          // If endpoint doesn't exist (404), inform user that feature is not available
          if (err.response?.status === 404) {
            console.log('Email change endpoint not available');
            toast.info('Email change feature is not available yet. Backend endpoint not implemented.');
            // If only email was changed (and name wasn't), prevent save completion
            if (!nameChanged) {
              setIsSaving(false);
              return;
            }
            // If name was also changed, continue to close edit mode
          } else {
            console.error('Failed to request email change:', err);
            toast.error(err.response?.data?.message || 'Failed to request email change');
            // Only prevent save if email change was the only change
            if (!nameChanged) {
              setIsSaving(false);
              return;
            }
            // If name was also changed, continue to close edit mode
          }
        }
      }

      setIsEditMode(false);
      setIsSaving(false);
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || 'Failed to save changes');
      setIsSaving(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setOtpError('Please enter the OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      const result = await verifyEmailChange(otp);
      // Use the updated user data from backend response
      const updatedEmail = result.email;
      
      // Update email in AuthContext and profileData with actual response
      updateUser({ email: updatedEmail });
      if (profileData) {
        setProfileData({ ...profileData, email: updatedEmail });
      }
      
      // Update editedEmail to reflect the change
      setEditedEmail(updatedEmail);
      
      // Mark OTP as verified but keep edit mode open
      setOtpVerified(true);
      setEmailVerificationStatus('verified');
      setOtp('');
      setOtpError(null);
      setDevOtp('');
      
      toast.success('Email verified successfully');
    } catch (err: any) {
      console.error('Failed to verify OTP:', err);
      const errorMessage = err.response?.data?.message || 'Invalid or expired OTP';
      setOtpError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const getRoleLabel = (role: string) => {
    if (role === ROLES.DOCTOR) return 'Doctor';
    if (role === ROLES.SUPERADMIN) return 'Super Admin';
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center space-y-4">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Only show error if it's a non-404 error and we don't have user data from AuthContext
  if (error && !user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="card max-w-md w-full">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <p className="font-medium">Error loading profile</p>
          </div>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const displayData = profileData || user;
  const role = displayData?.role || '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600">View your account information and role details</p>
        </div>
        {!isEditMode && (
          <button
            onClick={handleEditClick}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
          >
            <Edit2 className="h-4 w-4" />
            <span>Edit Profile</span>
          </button>
        )}
      </div>

      {/* Profile Card */}
      <div className="card">
        <div className="space-y-6">
          {/* User Avatar and Basic Info */}
          <div className="flex items-start space-x-6 pb-6 border-b border-gray-200">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="h-10 w-10 text-primary-600" />
              </div>
            </div>
            <div className="flex-1">
              {isEditMode ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="input-field w-full"
                    placeholder="Enter your name"
                  />
                </div>
              ) : (
                <h2 className="text-xl font-semibold text-gray-900">
                  {displayData?.name || 'User'}
                </h2>
              )}
              <div className="mt-2 flex items-center space-x-2">
                <Shield className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  {getRoleLabel(role)}
                </span>
              </div>
            </div>
          </div>

          {/* Common User Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              {isEditMode ? (
                <div className="space-y-3">
                  <input
                    type="email"
                    value={emailVerificationStatus === 'pending' ? (pendingEmail || displayData?.email || '') : editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    className="input-field w-full"
                    placeholder="Enter your email"
                    disabled={emailVerificationStatus === 'pending'}
                  />
                  {emailVerificationStatus === 'pending' && !otpVerified && (
                    <>
                      <p className="text-sm text-amber-600 flex items-center space-x-1">
                        <CheckCircle className="h-4 w-4" />
                        <span>Verification email sent to {pendingEmail}. Enter OTP below.</span>
                      </p>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Enter OTP sent to new email
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={otp}
                            onChange={(e) => {
                              setOtp(e.target.value);
                              setOtpError(null);
                            }}
                            className="input-field flex-1"
                            placeholder="Enter 6-digit OTP"
                            maxLength={6}
                            disabled={isVerifyingOtp}
                          />
                          <button
                            onClick={handleVerifyOtp}
                            disabled={isVerifyingOtp || !otp.trim()}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {isVerifyingOtp ? (
                              <>
                                <Loader className="h-4 w-4 inline animate-spin mr-2" />
                                Verifying...
                              </>
                            ) : (
                              'Verify OTP'
                            )}
                          </button>
                        </div>
                        {otpError && (
                          <p className="text-sm text-red-600 flex items-center space-x-1">
                            <AlertCircle className="h-4 w-4" />
                            <span>{otpError}</span>
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  {otpVerified && (
                    <p className="text-sm text-green-600 flex items-center space-x-1">
                      <CheckCircle className="h-4 w-4" />
                      <span>Email verified</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-gray-900">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{displayData?.email || 'N/A'}</span>
                  {emailVerificationStatus === 'pending' && (
                    <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      Pending verification
                    </span>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <div className="flex items-center space-x-2 text-gray-900">
                <Shield className="h-4 w-4 text-gray-400" />
                <span>{getRoleLabel(role)}</span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 p-3 rounded-md">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}

         

          {/* Role-Specific Sections */}
          {role === ROLES.DOCTOR && (
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Doctor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department / Specialization
                  </label>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editedDepartment}
                      onChange={(e) => setEditedDepartment(e.target.value)}
                      className="input-field w-full"
                      placeholder="Enter your department or specialization"
                      maxLength={100}
                    />
                  ) : (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Briefcase className="h-4 w-4 text-gray-400" />
                      <span>{profileData?.department || profileData?.specialization || 'Not specified'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience
                  </label>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Award className="h-4 w-4 text-gray-400" />
                    <span>{profileData?.experience || 'Not specified'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          

          {role === ROLES.SUPERADMIN && (
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Administration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Level
                  </label>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Shield className="h-4 w-4 text-gray-400" />
                    <span>{profileData?.accessLevel || 'Full System Access'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
           {/* Edit Mode Actions */}
           {isEditMode && (
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dev/QA OTP badge — only visible in non-production */}
      {emailVerificationStatus === 'pending' && !otpVerified && devOtp && process.env.NODE_ENV !== 'production' && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-900/90 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-mono backdrop-blur-sm">
          <span className="opacity-60">Dev OTP:</span>{' '}
          <span className="font-bold tracking-widest">{devOtp}</span>
        </div>
      )}
    </div>
  );
};

export default Profile;

